import { useEffect, useRef } from 'react'

/**
 * Normalizer for values coming from AI-injected metadata (e.g. AI Tools "Apply").
 * Default keeps booleans as-is and stringifies everything else.
 */
export type AiInjectedMetadataNormalizer = (value: unknown) => string | boolean

const defaultNormalizer: AiInjectedMetadataNormalizer = (value) =>
  typeof value === 'boolean' ? value : String(value)

export interface UseAiInjectedMetadataOptions<K extends string> {
  /** Metadata injected by another component (e.g. AI Metadata Generation). Only these keys are merged. */
  aiInjectedMetadata: Record<string, unknown> | null | undefined
  /** Only these keys are merged into form state; others are ignored. */
  allowedKeys: readonly K[]
  /** Called with filtered, normalized updates; typically merge into form state. */
  onApply: (updates: Partial<Record<K, string | boolean>>) => void
  /** Optional. Default: booleans preserved, rest stringified. */
  normalizer?: AiInjectedMetadataNormalizer
}

/**
 * Merges AI-injected metadata (from another component, e.g. AI Tools) into
 * section form state for allowed keys only. Shared by VideoContentInformationSection
 * and VideoContentSeoSection so the "another component meddling" behavior lives in one place.
 *
 * Usage:
 *   useAiInjectedMetadata({
 *     aiInjectedMetadata,
 *     allowedKeys: SEO_APPLY_KEYS,
 *     onApply: (updates) => setFormValues((prev) => ({ ...prev, ...updates })),
 *   })
 */
export function useAiInjectedMetadata<K extends string> ({
  aiInjectedMetadata,
  allowedKeys,
  onApply,
  normalizer = defaultNormalizer,
}: UseAiInjectedMetadataOptions<K>): void {
  const onApplyRef = useRef(onApply)
  onApplyRef.current = onApply

  useEffect(() => {
    if (!aiInjectedMetadata || Object.keys(aiInjectedMetadata).length === 0) return
    const allowed = new Set(allowedKeys)
    const updates: Partial<Record<K, string | boolean>> = {}
    Object.entries(aiInjectedMetadata).forEach(([key, value]) => {
      if (allowed.has(key as K) && value != null) {
        ;(updates as Record<string, string | boolean>)[key] = normalizer(value)
      }
    })
    if (Object.keys(updates).length > 0) {
      onApplyRef.current(updates)
    }
  }, [aiInjectedMetadata, allowedKeys, normalizer])
}
